import { CreativeXScore } from "hoxton-shared/enums/creativex";

import { BannerQaUserStatusLabelFilterMap } from "@/enums/banners";
import {
    CreativeXScoreFilters,
    PreflightStatusFilters,
    PublishedStatusFilter,
    BannerQaCommentedOnByFilter,
    BannerQaStatusFilter,
    BannerQaUserStatusFilter
} from "@/enums/filters";
import { PUBLISH_PROVIDERS, type PublishProvidersType } from "hoxton-shared/enums/exports";

type Filters = {
    selectedLanguages: string[];
    selectedSizes: string[];
    selectedEditableIdsByGroup: Record<string, string[]>;

    // Workflow filters
    sataliaStatus?: (typeof PreflightStatusFilters)[keyof typeof PreflightStatusFilters];
    creativeXStatus?: (typeof PreflightStatusFilters)[keyof typeof PreflightStatusFilters];
    creativeXScore?: (typeof CreativeXScoreFilters)[keyof typeof CreativeXScoreFilters];
    commentedOnBy?: (typeof BannerQaCommentedOnByFilter)[keyof typeof BannerQaCommentedOnByFilter];
    qaStatus?: (typeof BannerQaStatusFilter)[keyof typeof BannerQaStatusFilter];
    publishedStatus?: (typeof PublishedStatusFilter)[keyof typeof PublishedStatusFilter];
    reviewStatus?: (keyof typeof BannerQaUserStatusLabelFilterMap | "ALL")[];
    visibleTo?: string;
    hideDuplicates?: boolean;
    metadataSearch?: Record<string, string>;
};

export default function constructLibraryFiltersQueryVariables(filters: Filters) {
    return {
        filters: {
            editableGroups: constructEditableGroupFiltersQueryVariables(filters),
            masterTemplateIds: filters.selectedSizes,
            languages: filters.selectedLanguages.length ? filters.selectedLanguages : null
        },
        status: filters.qaStatus && filters.qaStatus !== BannerQaStatusFilter.All ? filters.qaStatus : null,
        userStatus: constructUserStatusFilterQueryVariables(filters),
        search: {
            metadata: constructMetadataSearchQueryVariables(filters)
        },
        publishedStatus: constructPublishedStatusFilterQueryVariables(filters),
        commentedOnBy:
            filters.commentedOnBy && filters.commentedOnBy !== BannerQaCommentedOnByFilter.All
                ? filters.commentedOnBy
                : null,
        visibleTo: filters.visibleTo && filters.visibleTo !== "ME" ? filters.visibleTo : [],
        dedup: filters.hideDuplicates ?? false,
        creativeXFilters: constructCreativeXFiltersQueryVariables(filters),
        sataliaFilters: constructSataliaFiltersQueryVariables(filters)
    };
}

function constructCreativeXFiltersQueryVariables(filters: Filters) {
    const [type, threshold] = filters.creativeXScore?.split("-") ?? [];

    const aboveScore = type === "OVER" ? Number(threshold) : CreativeXScore.MIN;
    const bellowScore = type === "UNDER" ? Number(threshold) - 0.01 : CreativeXScore.MAX;

    if (!filters.creativeXStatus || filters.creativeXStatus === PreflightStatusFilters.All) {
        return null;
    }

    const params: {
        status: (typeof PreflightStatusFilters)[keyof typeof PreflightStatusFilters];
        aboveScore?: number;
        bellowScore?: number;
    } = {
        status: filters.creativeXStatus
    };

    if (
        filters.creativeXStatus === PreflightStatusFilters.Completed &&
        filters.creativeXScore &&
        filters.creativeXScore !== CreativeXScoreFilters.All
    ) {
        params.aboveScore = aboveScore;
        params.bellowScore = bellowScore;
    }

    return params;
}

function constructSataliaFiltersQueryVariables(filters: Filters) {
    if (!filters.sataliaStatus || filters.sataliaStatus === PreflightStatusFilters.All) {
        return null;
    }

    return {
        status: filters.sataliaStatus
    };
}

function constructMetadataSearchQueryVariables(filters: Filters) {
    return Object.keys(filters.metadataSearch ?? {})?.length
        ? Object.keys(filters.metadataSearch ?? {})?.map(fieldId => ({
              fieldId,
              value: filters.metadataSearch?.[fieldId]
          }))
        : null;
}

function constructEditableGroupFiltersQueryVariables(filters: Filters) {
    const groupFilters = Object.keys(filters.selectedEditableIdsByGroup).map(name => ({
        name,
        valueIds: filters.selectedEditableIdsByGroup[name]
    }));

    return groupFilters.length ? groupFilters : null;
}

function constructPublishedStatusFilterQueryVariables(filters: Filters) {
    if (filters.publishedStatus === PublishedStatusFilter.All) {
        return null;
    }

    let platform: PublishProvidersType = PUBLISH_PROVIDERS.ALL;

    const published =
        filters.publishedStatus === PublishedStatusFilter.Published ||
        filters.publishedStatus === PublishedStatusFilter.PublishedToDoubleClick ||
        filters.publishedStatus === PublishedStatusFilter.PublishedToOpenDC;

    if (
        filters.publishedStatus === PublishedStatusFilter.PublishedToDoubleClick ||
        filters.publishedStatus === PublishedStatusFilter.NotPublishedToDoubleClick
    ) {
        platform = PUBLISH_PROVIDERS.DOUBLECLICK;
    }

    if (
        filters.publishedStatus === PublishedStatusFilter.PublishedToOpenDC ||
        filters.publishedStatus === PublishedStatusFilter.NotPublishedToOpenDC
    ) {
        platform = PUBLISH_PROVIDERS.OPENDC;
    }

    return {
        published,
        platform
    };
}

function constructUserStatusFilterQueryVariables(filters: Filters) {
    if (!filters.reviewStatus || filters.reviewStatus?.length === 0) {
        return null;
    }

    const filteredReviewStatus = filters.reviewStatus
        .filter(
            (status): status is keyof typeof BannerQaUserStatusLabelFilterMap => status !== BannerQaUserStatusFilter.All
        )
        .map(status => BannerQaUserStatusLabelFilterMap[status]);

    return filteredReviewStatus.length ? filteredReviewStatus : null;
}

export type ConstructedLibraryFilters = ReturnType<typeof constructLibraryFiltersQueryVariables>;
